import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, BadgeCheck, Handshake, Clock } from "lucide-react";
import { PublicHeader } from "../components/PublicHeader";
import { SiteFooter } from "../components/SiteFooter";
import { CityStrip } from "../components/CityStrip";
import { CITIES } from "../lib/cities";

export default function CityPage() {
  const { slug } = useParams();
  const city = CITIES.find((c) => c.slug === slug);

  useEffect(() => {
    if (city) document.title = `Recruiters in ${city.name} | The Ditch Careers`;
    window.scrollTo(0, 0);
    return () => { document.title = "The Ditch Careers"; };
  }, [city]);

  if (!city) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#fcfbf8]">
      <PublicHeader />
      <section className="bg-[#0d2f4d] grain relative">
        <div className="max-w-7xl mx-auto px-6 pt-28 pb-20">
          <p className="inline-flex items-center gap-2 font-heading text-xs sm:text-sm font-bold uppercase tracking-[0.35em] text-[#C1CC24]">
            <MapPin size={14} /> {city.state} · Australia
          </p>
          <h1 className="mt-4 text-4xl sm:text-6xl lg:text-7xl font-black uppercase text-white tracking-tighter leading-[0.95]" data-testid="city-page-heading">
            Recruiters in<br />
            <span className="font-accent font-medium normal-case italic text-[#C1CC24]">{city.name}.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/85 max-w-xl leading-relaxed">{city.blurb}</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link to="/signup/candidate" data-testid="city-cta-candidate"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C1CC24] text-[#0d2f4d] font-semibold px-8 py-4 hover:bg-[#aeb81f] transition-colors">
              I'm looking for work in {city.name} <ArrowRight size={16} />
            </Link>
            <Link to="/employers" data-testid="city-cta-employer"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white font-medium px-8 py-4 hover:bg-white/10 transition-colors">
              I'm hiring in {city.name}
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Handshake, title: "A recruiter in your corner", text: `Whether you're hiring or job hunting in ${city.name}, you deal with a real recruiter who knows the local market — not a job board algorithm.` },
            { icon: BadgeCheck, title: "Vetted, every time", text: "Work rights confirmed, references checked, credentials verified — before anyone reaches a shortlist." },
            { icon: Clock, title: "Fast to market", text: "Employer briefs go to market within 24–48 hours. Candidates get matched as roles land — often before they're ever advertised." },
          ].map(({ icon: Icon, title, text }) => (
            <div key={title} className="ditch-card p-8">
              <Icon size={24} className="text-[#7C8514]" />
              <h3 className="font-heading font-bold text-[#0d2f4d] text-lg mt-4">{title}</h3>
              <p className="text-sm text-[#51637a] mt-2 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <CityStrip exclude={city.slug} />
      <SiteFooter />
    </div>
  );
}
